/**
 * AI 件名アシスト ダイアログ (掲示板 Phase 4 / plan §4, §7-1)。
 *
 * assistBoardSubject が返した件名候補を一覧表示し、選択された候補を
 * 件名フィールドへ反映する。
 * - 件名 入力済み → 「ブラッシュアップ」した候補
 * - 件名 未入力 → 「本文から」生成した候補
 * どちらのモードかは呼び出し側 (BoardPostEditorPage) が判定してヘッダーに表示する。
 */
import { Sparkles } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';

interface SubjectAssistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** AI が生成した件名候補。 */
  suggestions: string[];
  /** 生成中フラグ (true の間はスピナー表示)。 */
  loading: boolean;
  /** 候補選択時のコールバック。選択後ダイアログは閉じる。 */
  onSelect: (text: string) => void;
  /**
   * true = 件名入力済み (ブラッシュアップ) / false = 件名未入力 (本文から生成)。
   * ヘッダーの説明文の出し分けに使用。
   */
  hasExistingTitle: boolean;
}

export function SubjectAssistDialog({
  open,
  onOpenChange,
  suggestions,
  loading,
  onSelect,
  hasExistingTitle,
}: SubjectAssistDialogProps) {
  const handleSelect = (text: string) => {
    onSelect(text);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-[var(--accent)]" />
            AI 件名アシスト
          </DialogTitle>
          <DialogDescription>
            {hasExistingTitle
              ? '入力済みの件名をブラッシュアップした候補です。使いたい候補を選んでください。'
              : '本文から件名候補を生成しました。使いたい候補を選んでください。'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10">
            <Spinner />
            <p className="text-xs text-[var(--text-muted)]">件名を生成しています…</p>
          </div>
        ) : suggestions.length === 0 ? (
          <p className="py-8 text-center text-xs text-[var(--text-muted)] italic">
            候補が見つかりませんでした。本文を入力してから再度お試しください。
          </p>
        ) : (
          <div className="space-y-2">
            {suggestions.map((text, idx) => (
              <button
                key={`${idx}-${text}`}
                type="button"
                onClick={() => handleSelect(text)}
                className="w-full text-left px-3 py-2.5 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] text-sm text-[var(--text-primary)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent-subtle)]"
              >
                {text}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
